/**
 * HIPImpactsTable Component
 *
 * A floating table inset for Hurricane Impact Predictions dashboard.
 * Displays impact forecasts by geographic region with sorting.
 */

import { useState, useMemo, useEffect } from "react";
import { ArrowUpDown, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import {
  VariableSelectionDropdown,
  type VariableOption,
} from "./VariableSelectionDropdown";
import type { ImpactForecast } from "@/api/types/storms";

interface HIPImpactsTableProps
  extends Omit<BaseInsetProps, "data" | "headers"> {
  impacts: ImpactForecast[];
  onRowClick?: (impact: ImpactForecast) => void;
  modelVariables?: {
    slug: string;
    name: string;
    units?: string | null;
    order?: number;
  }[];
  selectorVariables?: {
    id: string;
    label: string;
    units?: string | null;
  }[];
}

type TableRowData = Record<string, string | number | undefined> & {
  rowId: string;
  name: string;
};

const DEFAULT_DATA_COLUMNS = [
  "customersAffected",
  "incidents",
  "peakWindSpeed",
] as const;

const formatFallbackLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * HIPImpactsTable Component
 *
 * Displays hurricane impact predictions in a floating table overlay.
 * Similar to MapTableInset but specialized for HIP impact data.
 */
const HIPImpactsTable: React.FC<HIPImpactsTableProps> = ({
  impacts,
  loading = false,
  error = null,
  position = "bottom-left",
  width = "450px",
  height = "380px",
  title = "Impact Predictions",
  subtitle,
  onRowClick,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  panelMode = false,
  modelVariables = [],
  selectorVariables = [],
}) => {
  const [sortColumn, setSortColumn] = useState<string>("customersAffected");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [pageIndex, setPageIndex] = useState(0);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([
    "name",
    ...DEFAULT_DATA_COLUMNS,
  ]);
  const pageSize = 8;

  const dedupedImpactsByNodeId = useMemo(() => {
    const deduped = new Map<string, ImpactForecast>();
    impacts.forEach((impact) => {
      deduped.set(impact.geographic_node_id, impact);
    });
    return deduped;
  }, [impacts]);

  // Transform impacts into flat table format
  const tableData = useMemo(() => {
    if (!impacts || impacts.length === 0) return [];

    return Array.from(dedupedImpactsByNodeId.values()).map((impact) => ({
      rowId: impact.geographic_node_id,
      name: impact.geographic_node_name,
      customersAffected:
        impact.variable_values.customersAffected ??
        impact.variable_values.customersInterrupted ??
        0,
      peakWindSpeed: impact.variable_values.peakWindSpeed,
      incidents: impact.variable_values.incidents,
      ...impact.variable_values,
    })) as TableRowData[];
  }, [impacts, dedupedImpactsByNodeId]);

  // All available column definitions from model metadata, filtered by data presence.
  const allColumns = useMemo(() => {
    const cols: Array<{
      key: string;
      label: string;
      width: string;
      alwaysVisible?: boolean;
    }> = [
      {
        key: "name",
        label: "Prediction Area",
        width: "140px",
        alwaysVisible: true,
      },
    ];

    const availableDataKeys = Array.from(
      new Set(
        tableData.flatMap((row) =>
          Object.keys(row).filter(
            (key) =>
              key !== "rowId" &&
              key !== "name" &&
              row[key as keyof typeof row] !== undefined &&
              row[key as keyof typeof row] !== null,
          ),
        ),
      ),
    );

    const metadataBySlug = new Map(
      modelVariables.map((variable) => [variable.slug, variable] as const),
    );

    if (selectorVariables.length > 0) {
      const selectorVariablesById = new Map(
        selectorVariables.map((variable, index) => [
          variable.id,
          { ...variable, order: index },
        ]),
      );

      selectorVariables
        .map((variable) => variable.id)
        .filter((key) => availableDataKeys.includes(key))
        .forEach((key) => {
          const selectorVariable = selectorVariablesById.get(key);
          const metadata = metadataBySlug.get(key);
          const units = metadata?.units ?? selectorVariable?.units ?? null;
          const baseLabel = selectorVariable?.label ?? metadata?.name ?? formatFallbackLabel(key);
          const label = `${baseLabel}${units ? ` (${units})` : ""}`;
          const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
          cols.push({ key, label, width });
        });

      return cols;
    }

    const withMetadata = availableDataKeys
      .filter((key) => metadataBySlug.has(key))
      .map((key) => ({
        key,
        meta: metadataBySlug.get(key)!,
      }))
      .sort((a, b) => {
        const aOrder = a.meta.order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.meta.order ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.meta.name.localeCompare(b.meta.name);
      });

    const withoutMetadata = availableDataKeys
      .filter((key) => !metadataBySlug.has(key))
      .sort((a, b) => a.localeCompare(b));

    withMetadata.forEach(({ key, meta }) => {
      const label = `${meta.name}${meta.units ? ` (${meta.units})` : ""}`;
      const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
      cols.push({ key, label, width });
    });

    withoutMetadata.forEach((key) => {
      const label = formatFallbackLabel(key);
      const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
      cols.push({ key, label, width });
    });

    return cols;
  }, [tableData, modelVariables, selectorVariables]);

  // Visible columns based on selection
  const columns = useMemo(() => {
    return allColumns.filter(
      (col) => col.alwaysVisible || visibleColumnKeys.includes(col.key)
    );
  }, [allColumns, visibleColumnKeys]);

  const availableVariables = useMemo<VariableOption[]>(
    () =>
      allColumns
        .filter((col) => !col.alwaysVisible)
        .map((col, index) => {
          const meta = modelVariables.find((variable) => variable.slug === col.key);
          return {
          id: col.key,
          slug: col.key,
          name: col.label,
          units: meta?.units ?? null,
          order: meta?.order ?? index,
        };
        }),
    [allColumns, modelVariables]
  );

  const selectedVariables = useMemo(
    () => visibleColumnKeys.filter((key) => key !== "name"),
    [visibleColumnKeys]
  );

  // Reconcile selected columns when available columns change (e.g. model change).
  useEffect(() => {
    const availableDataKeys = allColumns
      .filter((col) => !col.alwaysVisible)
      .map((col) => col.key);

    setVisibleColumnKeys((prev) => {
      const prevDataKeys = prev.filter((key) => key !== "name");
      const validPrevDataKeys = prevDataKeys.filter((key) =>
        availableDataKeys.includes(key)
      );
      const removedUnavailable = validPrevDataKeys.length !== prevDataKeys.length;

      const shouldApplyDefaults =
        validPrevDataKeys.length === 0 &&
        availableDataKeys.length > 0 &&
        (prevDataKeys.length === 0 || removedUnavailable);

      const nextDataKeys = shouldApplyDefaults
        ? DEFAULT_DATA_COLUMNS.filter((key) => availableDataKeys.includes(key))
        : validPrevDataKeys;

      return ["name", ...nextDataKeys];
    });
  }, [allColumns]);

  // Keep sort column valid after model/data changes.
  useEffect(() => {
    const availableDataKeys = allColumns
      .filter((col) => !col.alwaysVisible)
      .map((col) => col.key);

    if (sortColumn === "name" || availableDataKeys.includes(sortColumn)) return;

    const nextSortKey =
      DEFAULT_DATA_COLUMNS.find((key) => availableDataKeys.includes(key)) || "name";
    setSortColumn(nextSortKey);
    setSortDirection("desc");
  }, [allColumns, sortColumn]);

  // Sort data
  const processedData = useMemo(() => {
    let result = [...tableData];

    // Apply sort
    if (sortColumn) {
      result.sort((a, b) => {
        const aVal = a[sortColumn as keyof typeof a];
        const bVal = b[sortColumn as keyof typeof b];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal);
        const bStr = String(bVal);
        return sortDirection === "asc"
          ? aStr.localeCompare(bStr)
          : bStr.localeCompare(aStr);
      });
    }

    return result;
  }, [tableData, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, pageIndex, pageSize]);

  const pageCount = Math.ceil(processedData.length / pageSize);

  useEffect(() => {
    setPageIndex(0);
  }, [impacts]);

  // Format cell value for display
  const formatCellValue = (value: any, key: string): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    return String(value);
  };

  // Handle sort toggle
  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(key);
      setSortDirection("desc");
    }
  };

  const escapeCSVValue = (value: string): string => {
    if (value.includes(",") || value.includes('"') || value.includes("\n")) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (!processedData || processedData.length === 0) return;

    const headers = columns.map((c) => escapeCSVValue(c.label));
    const rows = processedData.map((row) =>
      columns.map((col) =>
        escapeCSVValue(
          formatCellValue(row[col.key as keyof typeof row], col.key)
        )
      )
    );

    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join(
      "\n"
    );

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `hip_impacts_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <AbstractInset
      data={impacts}
      loading={loading}
      error={error}
      position={position}
      width={width}
      height={height}
      title={title}
      subtitle={subtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      panelMode={panelMode}
      containerId="hip-impacts-table-inset"
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">Impact Predictions</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>
              <strong>District</strong>: Geographic service area
            </li>
            <li>
              <strong>Customers Affected</strong>: Predicted customer outages
            </li>
            <li>Click column headers to sort</li>
          </ul>
        </div>
      }
    >
      {(insetState: InsetState) => (
        <div className="relative h-full min-h-[300px] flex flex-col">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-destructive/10 border-b border-destructive/20">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {/* Loading overlay: keeps inset height stable while hiding stale table rows */}
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-background/80 backdrop-blur-[1px]">
              <div className="inline-block h-4 w-4 border-2 border-t-primary rounded-full animate-spin" />
              <p className="mt-2 text-xs text-muted-foreground">
                Loading impacts...
              </p>
            </div>
          )}

          {/* Empty state - only when not loading */}
          {!loading && tableData.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-xs text-muted-foreground">
                No impact data available
              </p>
            </div>
          ) : (
            <div
              className={`overflow-x-auto flex flex-col h-full transition-opacity duration-150 ${loading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              aria-hidden={loading}
            >
              {/* Table controls */}
              <div className="px-3 flex items-center justify-end gap-2 py-2 flex-shrink-0">
                <div className="flex items-center gap-1">
                  {/* Columns dropdown */}
                  <VariableSelectionDropdown
                    availableVariables={availableVariables}
                    selectedVariables={selectedVariables}
                    onSelectedVariablesChange={(variables) =>
                      setVisibleColumnKeys(["name", ...variables])
                    }
                  />

                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 flex-shrink-0"
                    onClick={handleExportCSV}
                    title="Download as CSV"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="border-x border-t relative">
                <ScrollArea>
                  <div style={{ width: "max-content", minWidth: "100%" }}>
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                        <TableRow>
                          {columns.map((col) => (
                              <TableHead
                                key={col.key}
                                style={{ width: col.width }}
                              >
                                <Button
                                  variant="ghost"
                                  onClick={() => handleSort(col.key)}
                                  className="p-0 h-auto font-medium text-xs flex items-center gap-1"
                                >
                                  <span className="break-words">{col.label}</span>
                                  <ArrowUpDown className="h-3 w-3 flex-shrink-0" />
                                </Button>
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((row, idx) => (
                            <TableRow
                              key={row.rowId || row.name || idx}
                              onClick={() => {
                                const original = dedupedImpactsByNodeId.get(
                                  row.rowId
                                );
                                if (original && onRowClick)
                                  onRowClick(original);
                              }}
                              className={
                                onRowClick
                                  ? "cursor-pointer hover:bg-muted/50"
                                  : ""
                              }
                            >
                              {columns.map((col) => (
                                  <TableCell
                                    key={col.key}
                                    className="p-2 text-xs"
                                    style={{ width: col.width }}
                                  >
                                    <div className="break-words whitespace-normal overflow-hidden">
                                      {formatCellValue(
                                        row[col.key as keyof typeof row],
                                        col.key
                                      )}
                                    </div>
                                  </TableCell>
                                ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              className="h-24 text-center text-xs"
                            >
                              No results.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-3 py-1 flex-shrink-0 bg-background border-x border-b">
                <div className="space-x-2 flex items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      setPageIndex((prev) => Math.max(0, prev - 1))
                    }
                    disabled={pageIndex === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-xs">
                    Page {pageIndex + 1} of {pageCount || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() =>
                      setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))
                    }
                    disabled={pageIndex >= pageCount - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </AbstractInset>
  );
};

export { HIPImpactsTable };
export type { HIPImpactsTableProps };
